/**
 * 🧹 DATA CLEANUP UTILITY
 * Helper functions to clear user data for fresh signup
 */

export interface DataCleanupResult {
  success: boolean;
  clearedItems: string[];
  errors?: string[];
}

/**
 * Clear all localStorage data for a specific email/user
 */
export const clearUserLocalStorageData = (email: string): DataCleanupResult => {
  const result: DataCleanupResult = {
    success: true,
    clearedItems: [],
    errors: []
  };

  try {
    // Get all localStorage keys
    const allKeys = Object.keys(localStorage);
    
    // Clear authentication tokens
    const authKeys = ['token', 'refreshToken', 'user', 'auth'];
    authKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          // Check if the stored data contains the email
          if (value.includes(email) || key === 'token' || key === 'refreshToken') {
            localStorage.removeItem(key);
            result.clearedItems.push(`localStorage: ${key}`);
          }
        } catch (e) {
          // If it's not JSON, just check string content
          if (value.includes(email)) {
            localStorage.removeItem(key);
            result.clearedItems.push(`localStorage: ${key}`);
          }
        }
      }
    });

    // Clear verification data (format: verification-${userId})
    const verificationKeys = allKeys.filter(key => key.startsWith('verification-'));
    verificationKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          const data = JSON.parse(value);
          // Check if verification data contains the email
          if (JSON.stringify(data).includes(email)) {
            localStorage.removeItem(key);
            result.clearedItems.push(`localStorage: ${key}`);
          }
        } catch (e) {
          result.errors?.push(`Failed to parse ${key}: ${e}`);
        }
      }
    });

    // Clear any other user-related keys that might contain email
    const userRelatedKeys = allKeys.filter(key => 
      key.includes('user') || 
      key.includes('profile') || 
      key.includes('verification') ||
      key.includes('booking') ||
      key.includes('driver')
    );

    userRelatedKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value && value.includes(email)) {
        localStorage.removeItem(key);
        result.clearedItems.push(`localStorage: ${key}`);
      }
    });

    return result;

  } catch (error) {
    result.success = false;
    result.errors = [error instanceof Error ? error.message : 'Unknown error'];
    console.error('❌ LocalStorage cleanup failed:', error);
    return result;
  }
};

/**
 * Clear all sessionStorage data for a specific email/user
 */
export const clearUserSessionStorageData = (email: string): DataCleanupResult => {
  const result: DataCleanupResult = {
    success: true,
    clearedItems: [],
    errors: []
  };

  try {
    const allKeys = Object.keys(sessionStorage);
    
    allKeys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value && (value.includes(email) || key === 'token' || key === 'auth')) {
        sessionStorage.removeItem(key);
        result.clearedItems.push(`sessionStorage: ${key}`);
      }
    });

    return result;

  } catch (error) {
    result.success = false;
    result.errors = [error instanceof Error ? error.message : 'Unknown error'];
    console.error('❌ SessionStorage cleanup failed:', error);
    return result;
  }
};

/**
 * Clear all browser storage data for complete fresh start
 */
export const clearAllBrowserData = (): DataCleanupResult => {
  const result: DataCleanupResult = {
    success: true,
    clearedItems: [],
    errors: []
  };

  try {
    // Clear localStorage (environment is auto-detected from hostname, no need to preserve)
    const localStorageCount = localStorage.length;
    localStorage.clear();
    result.clearedItems.push(`localStorage: cleared all ${localStorageCount} items`);

    // Clear sessionStorage
    const sessionStorageCount = sessionStorage.length;
    sessionStorage.clear();
    result.clearedItems.push(`sessionStorage: cleared all ${sessionStorageCount} items`);

    return result;

  } catch (error) {
    result.success = false;
    result.errors = [error instanceof Error ? error.message : 'Unknown error'];
    console.error('❌ Browser storage cleanup failed:', error);
    return result;
  }
};

/**
 * Generate backend cleanup commands for the specific email
 */
export const generateBackendCleanupCommands = (email: string): string[] => {
  return [
    `-- SQL Commands to clean up backend data for: ${email}`,
    `-- ⚠️ CAUTION: These commands will permanently delete all user data`,
    ``,
    `-- 1. Find the user ID first`,
    `SELECT id, email, created_at FROM users WHERE email = '${email}';`,
    ``,
    `-- 2. Delete verification records (replace {USER_ID} with actual user ID)`,
    `DELETE FROM driver_verifications WHERE user_id = '{USER_ID}';`,
    `DELETE FROM user_verifications WHERE user_id = '{USER_ID}';`,
    ``,
    `-- 3. Delete user sessions and tokens`,
    `DELETE FROM user_sessions WHERE user_id = '{USER_ID}';`,
    `DELETE FROM refresh_tokens WHERE user_id = '{USER_ID}';`,
    ``,
    `-- 4. Delete bookings and related data`,
    `DELETE FROM bookings WHERE user_id = '{USER_ID}' OR host_id = '{USER_ID}';`,
    `DELETE FROM driver_applications WHERE user_id = '{USER_ID}';`,
    ``,
    `-- 5. Delete uploaded files/documents`,
    `DELETE FROM user_documents WHERE user_id = '{USER_ID}';`,
    ``,
    `-- 6. Finally delete the user record`,
    `DELETE FROM users WHERE email = '${email}';`,
    ``,
    `-- 7. Verify deletion`,
    `SELECT COUNT(*) as remaining_records FROM users WHERE email = '${email}';`,
  ];
};